import React, { useState } from 'react';
import { leaseApi, rouFinAlyzerApi } from '../services/api';
import '../styles/Modal.css';

function GenerateLeaseAnalysisModal({ entity, onClose, onComplete }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [outputBlob, setOutputBlob] = useState(null);

  const [pushLoading, setPushLoading] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState(null);

  // Optional manual ROU movements — EL group entities only. Each value is a signed
  // delta added to the computed Finalyzer Input row.
  const isELGroupEntity = entity && entity.lease_entity_group === 'EL';
  const [enableManualAdj, setEnableManualAdj] = useState(false);
  const ROU_MANUAL_FIELDS = [
    { key: 'gbAddition', label: 'GB Addition' },
    { key: 'adDepreciation', label: 'AD Depreciation' },
    { key: 'gbDisposal', label: 'GB Disposal' },
    { key: 'adDisposal', label: 'AD Disposal' },
    { key: 'gbAdjustment', label: 'GB Adjustment' },
    { key: 'adAdjustment', label: 'AD Adjustment' },
  ];
  const [manualValues, setManualValues] = useState({
    gbAddition: '', adDepreciation: '', gbDisposal: '',
    adDisposal: '', gbAdjustment: '', adAdjustment: '',
  });
  const setManualValue = (key, val) =>
    setManualValues(prev => ({ ...prev, [key]: val }));

  // Build the {key: number} payload, or null when not applicable / nothing entered.
  const buildRouManualAdjustment = () => {
    if (!isELGroupEntity || !enableManualAdj) return null;
    const out = {};
    let any = false;
    ROU_MANUAL_FIELDS.forEach(({ key }) => {
      const raw = (manualValues[key] || '').trim();
      if (raw === '') return;
      const n = parseFloat(raw);
      if (!isNaN(n)) { out[key] = n; any = true; }
    });
    return any ? out : null;
  };

  const handleCalculate = async () => {
    if (!startDate || !endDate) {
      setError('Please enter both start date and end date');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    setCalculating(true);
    setError(null);
    setResult(null);
    setPushResult(null);
    setPushError(null);

    try {
      const data = await leaseApi.calculateLeaseAnalysis(
        entity.name, startDate, endDate, null, buildRouManualAdjustment()
      );
      setResult(data);
      const blob = await leaseApi.downloadLeaseOutput(
        entity.name, startDate, endDate, data.output_timestamp
      );
      setOutputBlob(blob);
    } catch (err) {
      setError(err.message || 'Failed to calculate lease analysis');
    } finally {
      setCalculating(false);
    }
  };

  const handleDownload = () => {
    if (!outputBlob || !result) return;
    const url = window.URL.createObjectURL(outputBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lease_analysis_${entity.name}_${result.period}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handlePushToFinalyzer = async () => {
    setPushLoading(true);
    setPushResult(null);
    setPushError(null);
    try {
      const res = await rouFinAlyzerApi.push(entity.name, endDate);
      if (!res.success) throw new Error(res.error || 'Push failed');
      setPushResult({ records: res.records_pushed, message: res.message });
    } catch (err) {
      setPushError(err.message || 'Push to FinAlyzer failed');
    } finally {
      setPushLoading(false);
    }
  };

  const handleClose = () => {
    if (result && onComplete) onComplete();
    else onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '680px' }}>
        <div className="modal-header">
          <h2>Generate Lease Analysis</h2>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          {result ? (
            <div>
              {/* Success summary */}
              <div className="alert alert-success">
                Successfully generated lease analysis!
                <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                  <li>Period: {result.summary.start_date} to {result.summary.end_date}</li>
                  <li>Total Leases: {result.summary.lease_count}</li>
                </ul>
                <button
                  className="btn btn-primary"
                  onClick={handleDownload}
                  style={{ marginTop: '15px' }}
                >
                  Download Lease Analysis
                </button>
              </div>

              {/* Push to FinAlyzer */}
              <div style={rouSectionStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <span style={rouBadgeStyle}>FA</span>
                  <strong style={{ fontSize: '15px', color: '#1F4E79' }}>Push to FinAlyzer</strong>
                </div>
                <p style={{ fontSize: '13px', color: '#444', margin: '0 0 14px', lineHeight: '1.5' }}>
                  The output file includes a <strong>Finalyzer Input</strong> tab with GB (Addition),
                  AD (Depreciation), Disposal, and their offset rows. Click below to push all
                  non-zero rows to FinAlyzer via <code>postScheduleData</code>.
                </p>

                {pushResult ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button className="btn btn-primary" disabled style={{ background: '#4caf50', minWidth: '180px' }}>
                      Pushed ✓
                    </button>
                    <span style={{ fontSize: '13px', color: '#2e7d32' }}>
                      <strong>{pushResult.records}</strong> records pushed successfully.
                    </span>
                  </div>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={handlePushToFinalyzer}
                    disabled={pushLoading}
                    style={{ minWidth: '180px' }}
                  >
                    {pushLoading ? 'Pushing...' : 'Push to FinAlyzer'}
                  </button>
                )}

                {pushError && (
                  <div className="alert alert-error" style={{ marginTop: '10px', fontSize: '13px' }}>
                    {pushError}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <p>Calculate lease analysis for {entity.name}</p>

              <div className="form-group">
                <label htmlFor="start-date">Start Date</label>
                <input
                  type="date"
                  id="start-date"
                  className="form-control"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  disabled={calculating}
                />
              </div>

              <div className="form-group">
                <label htmlFor="end-date">End Date</label>
                <input
                  type="date"
                  id="end-date"
                  className="form-control"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  disabled={calculating}
                />
              </div>

              {isELGroupEntity && (
                <div style={manualAdjSectionStyle}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: enableManualAdj ? '12px' : 0 }}>
                    <input
                      type="checkbox"
                      checked={enableManualAdj}
                      onChange={e => setEnableManualAdj(e.target.checked)}
                      disabled={calculating}
                    />
                    <strong style={{ fontSize: '14px', color: '#1F4E79' }}>Add manual ROU adjustment</strong>
                  </label>

                  {enableManualAdj && (
                    <>
                      <p style={{ fontSize: '12px', color: '#555', margin: '0 0 12px', lineHeight: '1.5' }}>
                        Each value is a signed delta <strong>added to the computed balance</strong> of that
                        FinAlyzer Input row (first asset category). The <strong>ROU Manual Adjustment</strong> tab
                        shows Computed + Manual = Final, and Final is what gets pushed to FinAlyzer.
                        Negatives allowed; leave blank for 0.
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        {ROU_MANUAL_FIELDS.map(({ key, label }) => (
                          <div className="form-group" key={key} style={{ marginBottom: 0 }}>
                            <label htmlFor={`rou-${key}`}>{label}</label>
                            <input
                              type="number"
                              id={`rou-${key}`}
                              className="form-control"
                              step="any"
                              placeholder="0.00"
                              value={manualValues[key]}
                              onChange={e => setManualValue(key, e.target.value)}
                              disabled={calculating}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="alert alert-info" style={{ marginTop: '15px' }}>
                <strong>Note:</strong> The analysis will generate a multi-sheet Excel file with:
                <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                  <li>Individual lease schedules (one sheet per lease)</li>
                  <li>Summary of lease movements for the period</li>
                  <li>Maturity profile by lease</li>
                  <li>Finalyzer Input tab — GB, AD, Disposal + offset rows ready to push</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {!result && (
            <button
              className="btn btn-primary"
              onClick={handleCalculate}
              disabled={!startDate || !endDate || calculating}
            >
              {calculating ? 'Calculating...' : 'Generate'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

const rouSectionStyle = {
  border: '1px solid #b3d1f0',
  borderRadius: '8px',
  padding: '16px 18px',
  marginTop: '16px',
  background: '#f5f9ff',
};

const manualAdjSectionStyle = {
  border: '1px solid #d9c7ec',
  borderRadius: '8px',
  padding: '14px 16px',
  marginTop: '15px',
  background: '#faf7fe',
};

const rouBadgeStyle = {
  background: '#1F4E79',
  color: '#fff',
  borderRadius: '6px',
  padding: '2px 8px',
  fontSize: '12px',
  fontWeight: 'bold',
  flexShrink: 0,
};

export default GenerateLeaseAnalysisModal;
